import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { errMsg } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Send, PhoneOff, Bot, User, Mic, MicOff, Phone } from "lucide-react";

const STATUS_LABELS = {
  idle: "Ready",
  connecting: "Connecting…",
  listening: "Listening",
  speaking: "AI speaking",
  ended: "Call ended",
  error: "Connection error",
};

const fmtDur = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export default function TestReceptionist() {
  const [callId, setCallId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const idRef = useRef(0);
  const nextId = () => ++idRef.current;

  const vapiRef = useRef(null);
  const timerRef = useRef(null);
  const [vStatus, setVStatus] = useState("idle");
  const [muted, setMuted] = useState(false);
  const [vLines, setVLines] = useState([]);
  const [elapsed, setElapsed] = useState(0);
  const [vReport, setVReport] = useState(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);
  useEffect(() => () => { try { vapiRef.current?.stop(); } catch (e) { console.warn("vapi cleanup stop failed:", e); } clearInterval(timerRef.current); }, []);

  const loadReport = async (id) => {
    try {
      const r = await api.get(`/test-call/${id}`);
      setVReport(r.data);
    } catch (e) { console.warn("test-call summary load failed:", e); }
  };

  const startVoice = async () => {
    setVStatus("connecting"); setVLines([]); setVReport(null); setElapsed(0); setMuted(false);
    try {
      const cfg = await api.get("/test-call/config");
      if (!cfg.data.has_assistant) {
        toast.error("Set up your AI receptionist first — go to Settings and click “Set up my AI receptionist”.");
        setVStatus("idle"); return;
      }
      if (!cfg.data.vapi_public_key) {
        toast.error("Voice testing isn't available right now — please contact support.");
        setVStatus("error"); return;
      }
      const r = await api.post("/test-call");
      const { default: Vapi } = await import("@vapi-ai/web");  // lazy: keep the SDK off the public pages
      const v = new Vapi(cfg.data.vapi_public_key);
      vapiRef.current = v;
      v.on("call-start", () => {
        setVStatus("listening");
        timerRef.current = setInterval(() => setElapsed(s => s + 1), 1000);
      });
      v.on("speech-start", () => setVStatus("speaking"));
      v.on("speech-end", () => setVStatus("listening"));
      v.on("call-end", () => {
        clearInterval(timerRef.current);
        setVStatus("ended"); setMuted(false);
        loadReport(r.data.call_id);
      });
      v.on("error", (e) => {
        console.warn("vapi error", e);
        clearInterval(timerRef.current);
        setVStatus("error");
      });
      v.on("message", (m) => {
        if (m.type === "transcript" && m.transcriptType === "final" && m.transcript) {
          setVLines(ls => [...ls, { id: nextId(), role: m.role, text: m.transcript }]);
        }
      });
      await v.reconnect({ id: r.data.call_id, webCallUrl: r.data.web_call_url });
    } catch (e) {
      clearInterval(timerRef.current);
      setVStatus("error");
      toast.error(errMsg(e, "Could not start the test call — check microphone permission and try again"));
    }
  };

  const toggleMute = () => {
    const next = !muted;
    vapiRef.current?.setMuted(next);
    setMuted(next);
  };

  const endVoice = () => {
    try { vapiRef.current?.stop(); } catch (e) { console.warn("vapi stop failed:", e); }
    clearInterval(timerRef.current);
    setVStatus("ended");
  };

  const send = async (e) => {
    e?.preventDefault();
    if (!text.trim() || loading) return;
    const userMsg = text;
    setMessages(m => [...m, { id: nextId(), role: "customer", text: userMsg }]);
    setText("");
    setLoading(true);
    try {
      const r = await api.post("/receptionist/turn", { call_id: callId, message: userMsg });
      setCallId(r.data.call_id);
      setMessages(m => [...m, { id: nextId(), role: "ai", text: r.data.reply, actions: r.data.actions }]);
    } catch (err) {
      toast.error(errMsg(err, "AI error"));
      setMessages(m => [...m, { id: nextId(), role: "ai", text: "(error responding)" }]);
    } finally { setLoading(false); }
  };

  const endCall = async () => {
    if (!callId) return;
    try {
      await api.post("/receptionist/end", { call_id: callId });
      toast.success("Call ended and summarized");
      setCallId(null); setMessages([]);
      await api.patch("/business", { onboarding: { test: true } }).catch((err) => console.warn("onboarding marker failed:", err));
    } catch { toast.error("Failed to end"); }
  };

  const inCall = ["connecting", "listening", "speaking"].includes(vStatus);

  return (
    <div>
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Test my AI receptionist</h1>
        <p className="text-slate-600 mt-1">Try a live conversation before going live.</p>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-6 max-w-3xl" data-testid="voice-test-card">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${inCall ? "bg-emerald-50" : "bg-blue-50"}`}>
              <Phone className={`w-5 h-5 ${inCall ? "text-emerald-600" : "text-blue-600"}`} />
            </div>
            <div>
              <div className="font-heading font-semibold text-lg">Call AI Receptionist</div>
              <div className="text-sm text-slate-500">This is a private test and will not answer real customer calls.</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span data-testid="voice-status" className={`text-xs px-2.5 py-1 rounded-full font-medium ${
              vStatus === "listening" ? "bg-emerald-50 text-emerald-700" :
              vStatus === "speaking" ? "bg-blue-50 text-blue-700" :
              vStatus === "error" ? "bg-red-50 text-red-700" : "bg-slate-100 text-slate-600"}`}>
              {STATUS_LABELS[vStatus]}
            </span>
            {(inCall || vStatus === "ended") && elapsed > 0 && (
              <span data-testid="voice-duration" className="text-xs text-slate-500 font-mono">{fmtDur(elapsed)}</span>
            )}
          </div>
        </div>

        <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-xl p-3" data-testid="test-mode-notice">
          Test mode only. This conversation will not answer your live business number or contact real customers. Bookings are simulated and nothing is billed.
        </p>

        <div className="mt-4 flex gap-2">
          {!inCall ? (
            <Button data-testid="voice-call-btn" onClick={startVoice} className="rounded-xl bg-blue-600 hover:bg-blue-700">
              <Mic className="w-4 h-4 mr-2" />Call AI Receptionist
            </Button>
          ) : (
            <>
              <Button data-testid="voice-mute-btn" variant="outline" onClick={toggleMute} className="rounded-xl">
                {muted ? <><Mic className="w-4 h-4 mr-2" />Unmute</> : <><MicOff className="w-4 h-4 mr-2" />Mute</>}
              </Button>
              <Button data-testid="voice-end-btn" onClick={endVoice} className="rounded-xl bg-red-600 hover:bg-red-700">
                <PhoneOff className="w-4 h-4 mr-2" />End call
              </Button>
            </>
          )}
        </div>
        {vStatus === "error" && (
          <p className="mt-3 text-xs text-red-600" data-testid="voice-error-msg">
            Couldn't connect. Your browser needs microphone permission — allow it and try again.
          </p>
        )}

        {vLines.length > 0 && (
          <div className="mt-4 border border-slate-200 rounded-xl p-4 max-h-56 overflow-y-auto space-y-2 bg-slate-50" data-testid="voice-transcript">
            {vLines.map((l) => (
              <div key={l.id} className="text-sm"><span className="font-medium text-slate-500">{l.role === "assistant" ? "AI" : "You"}:</span> {l.text}</div>
            ))}
          </div>
        )}

        {vStatus === "ended" && (
          <div className="mt-4 border border-slate-200 rounded-xl p-4" data-testid="voice-report">
            <div className="text-sm font-semibold">Test call summary</div>
            {vReport ? (
              <div className="mt-2 space-y-2 text-sm text-slate-600">
                <div>Connection: <span className="text-emerald-700 font-medium" data-testid="report-connection">succeeded</span>
                  {vReport.duration_sec > 0 && <> · duration {fmtDur(vReport.duration_sec)}</>}</div>
                {vReport.summary && <p className="text-slate-600" data-testid="report-summary">{vReport.summary}</p>}
                {vReport.actions?.length > 0 && (
                  <div data-testid="report-actions">
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mt-2">Receptionist actions</div>
                    {vReport.actions.map((a, i) => (
                      <div key={`${a.name}-${i}`} className="text-xs mt-1">
                        → {a.name}: {a.result?.simulated || a.result?.test_mode
                          ? <span className="text-amber-700">simulated (nothing sent or booked)</span>
                          : <span className="text-emerald-700">completed</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : <div className="text-sm text-slate-400 mt-2">Loading summary…</div>}
            <p className="mt-3 text-xs text-slate-500">Passing a test call does not mean your business is live — click <b>Go live</b> in Settings when you're ready.</p>
          </div>
        )}
      </div>

      <div className="mt-8">
        <div className="flex items-center justify-between max-w-3xl">
          <div>
            <h2 className="text-xl font-bold tracking-tight">Text simulator</h2>
            <p className="text-slate-500 text-sm mt-0.5">Prefer typing? Chat with your receptionist here.</p>
          </div>
          {callId && <Button data-testid="end-call-btn" variant="outline" onClick={endCall} className="rounded-xl"><PhoneOff className="w-4 h-4 mr-2" />End & summarize</Button>}
        </div>

        <div className="mt-4 bg-white border border-slate-200 rounded-2xl overflow-hidden max-w-3xl">
          <div className="h-[420px] overflow-y-auto p-6 space-y-4 bg-slate-50" data-testid="chat-window">
            {messages.length === 0 && (
              <div className="text-center text-slate-500 pt-20">
                <Bot className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="text-sm">Type a message to start. Try "How much is a haircut?" or "Can I book tomorrow at 3?"</p>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={m.id} className={`flex gap-3 ${m.role === "customer" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${i}`}>
                {m.role === "ai" && <div className="w-8 h-8 rounded-full bg-blue-600 flex-shrink-0 flex items-center justify-center"><Bot className="w-4 h-4 text-white" /></div>}
                <div className={`max-w-md rounded-2xl px-4 py-2.5 text-sm ${m.role === "customer" ? "bg-slate-950 text-white" : "bg-white border border-slate-200 text-slate-900"}`}>
                  {m.text}
                  {m.actions && m.actions.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-slate-200 text-xs text-slate-500">
                      {m.actions.map((a, j) => <div key={`${m.id}-${a.name}-${j}`}>→ {a.name}({Object.keys(a.args || {}).length} args) · {a.result?.ok !== false ? "✓" : "✗"}</div>)}
                    </div>
                  )}
                </div>
                {m.role === "customer" && <div className="w-8 h-8 rounded-full bg-slate-300 flex-shrink-0 flex items-center justify-center"><User className="w-4 h-4 text-slate-700" /></div>}
              </div>
            ))}
            {loading && <div className="text-xs text-slate-500 pl-11">Ava is typing…</div>}
            <div ref={bottomRef} />
          </div>
          <form onSubmit={send} className="border-t border-slate-200 p-3 flex gap-2">
            <Input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Type your message…" className="flex-1" />
            <Button data-testid="chat-send-btn" type="submit" disabled={loading} className="rounded-xl bg-blue-600 hover:bg-blue-700"><Send className="w-4 h-4" /></Button>
          </form>
        </div>
      </div>
    </div>
  );
}
